import { createTheme, alpha } from "@mui/material/styles";

// Thème back-office aligné sur l'identité des apps ioUman (pro / patient) :
// vert #1B8550, titres en Sora, texte en DM Sans, fonds doux (#F5F7F5),
// cartes arrondies (12–16 px) et bordures légères (#E8EEE8).

export const BRAND = {
  green: "#1B8550",
  greenDark: "#157040",
  greenDeep: "#0D2B1A",
  greenSoft: "#EEF7F2",
  greenBright: "#4ADE80",
  bg: "#F5F7F5",
  border: "#E8EEE8",
  ink: "#0D1A0D",
  inkSoft: "#4B5563",
  muted: "#6B7280",
};

const HEADING = '"Sora", "DM Sans", "Helvetica", "Arial", sans-serif';
const BODY = '"DM Sans", "Helvetica", "Arial", sans-serif';

const buildTheme = (mode) => {
  const dark = mode === "dark";
  const bg = dark ? "#0F1511" : BRAND.bg;
  const paper = dark ? "#161E19" : "#FFFFFF";
  const border = dark ? "rgba(255,255,255,0.08)" : BRAND.border;
  const ink = dark ? "#E8EEE9" : BRAND.ink;
  const inkSoft = dark ? "#A3B0A7" : BRAND.inkSoft;
  const primary = dark ? "#34A46B" : BRAND.green;
  const headBg = dark ? "#1A231D" : "#F8FAF8";
  const hover = dark ? alpha("#34A46B", 0.08) : alpha(BRAND.green, 0.04);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: primary,
        light: dark ? "#5BC48D" : "#3FA36F",
        dark: dark ? "#1B8550" : BRAND.greenDark,
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: dark ? "#8FB8A2" : "#2F4A3A",
        contrastText: "#FFFFFF",
      },
      success: { main: "#16A34A", light: "#4ADE80", dark: "#15803D" },
      error: { main: "#DC2626", light: "#F87171", dark: "#B91C1C" },
      warning: { main: "#D97706", light: "#FBBF24", dark: "#B45309" },
      info: { main: "#2563EB", light: "#60A5FA", dark: "#1D4ED8" },
      background: { default: bg, paper },
      text: { primary: ink, secondary: inkSoft, disabled: dark ? "#5D6A61" : "#9CA3AF" },
      divider: border,
      action: { hover },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: BODY,
      fontSize: 14,
      h1: { fontFamily: HEADING, fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontFamily: HEADING, fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontFamily: HEADING, fontWeight: 700, letterSpacing: "-0.015em" },
      h4: { fontFamily: HEADING, fontWeight: 700, letterSpacing: "-0.01em", fontSize: "1.65rem" },
      h5: { fontFamily: HEADING, fontWeight: 700, fontSize: "1.3rem" },
      h6: { fontFamily: HEADING, fontWeight: 600, fontSize: "1.05rem" },
      subtitle1: { fontFamily: HEADING, fontWeight: 600 },
      subtitle2: { fontFamily: HEADING, fontWeight: 600, fontSize: "0.9rem" },
      overline: { fontWeight: 600, letterSpacing: "0.08em", fontSize: "0.68rem" },
      button: { fontWeight: 600, textTransform: "none" },
    },
    // Clés propres à react-admin (largeur du menu latéral).
    sidebar: { width: 264, closedWidth: 64 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: bg,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "a": { color: primary },
          "::selection": { background: alpha(BRAND.green, 0.18) },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, fontWeight: 600, paddingInline: 14 },
          sizeSmall: { paddingInline: 10 },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: 14 },
          outlined: { borderColor: border },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${border}`,
            boxShadow: dark ? "none" : "0 1px 2px rgba(13,26,13,0.04)",
          },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 20, "&:last-child": { paddingBottom: 20 } } },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: {
            backgroundColor: paper,
            color: ink,
            borderBottom: `1px solid ${border}`,
            boxShadow: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderRight: "none", backgroundColor: "transparent" },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const base = { fontWeight: 600, borderRadius: 8, fontSize: 12 };
            const c = ownerState.color;
            if (!c || c === "default") {
              return ownerState.variant === "outlined"
                ? base
                : { ...base, backgroundColor: dark ? "rgba(255,255,255,0.08)" : "#EEF1EE", color: inkSoft };
            }
            const pal = theme.palette[c];
            if (!pal) return base;
            if (ownerState.variant === "outlined") {
              return { ...base, borderColor: alpha(pal.main, 0.35), color: dark ? pal.light : pal.dark };
            }
            // Pastille « douce » : fond teinté + texte foncé (lisible, moins criard).
            return {
              ...base,
              backgroundColor: alpha(pal.main, dark ? 0.2 : 0.12),
              color: dark ? pal.light : pal.dark,
            };
          },
          sizeSmall: { height: 24 },
          label: { paddingInline: 10 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${border}`, paddingTop: 10, paddingBottom: 10 },
          head: {
            fontWeight: 600,
            fontSize: 12,
            color: inkSoft,
            backgroundColor: headBg,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
          },
          sizeSmall: { paddingTop: 9, paddingBottom: 9 },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&.MuiTableRow-hover:hover, &:hover": { backgroundColor: hover },
            "&:last-child td": { borderBottom: 0 },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10, backgroundColor: paper },
          notchedOutline: { borderColor: dark ? "rgba(255,255,255,0.14)" : "#D8E0D8" },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: dark ? "rgba(255,255,255,0.05)" : "#F1F4F1",
            "&:before, &:after": { display: "none" },
            "&:hover": { backgroundColor: dark ? "rgba(255,255,255,0.08)" : "#EAEFEA" },
            "&.Mui-focused": { backgroundColor: dark ? "rgba(255,255,255,0.08)" : "#EAEFEA" },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44, borderBottom: `1px solid ${border}` },
          indicator: { height: 3, borderRadius: 3 },
          scrollButtons: { "&.Mui-disabled": { opacity: 0.3 } },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            minHeight: 44,
            fontSize: 14,
            paddingInline: 14,
            color: inkSoft,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, fontSize: 12, backgroundColor: dark ? "#2A352E" : BRAND.ink },
        },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${border}`,
            borderRadius: 12,
            "&:before": { display: "none" },
            overflow: "hidden",
          },
        },
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
      MuiMenuItem: { styleOverrides: { root: { borderRadius: 8, marginInline: 6 } } },
      MuiToolbar: { styleOverrides: { root: {} } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 4 } } },

      // ── react-admin ───────────────────────────────────────────────────
      RaLayout: {
        styleOverrides: {
          root: {
            backgroundColor: bg,
            minWidth: 0,
            "& .RaLayout-appFrame, & .RaLayout-contentWithSidebar": { minWidth: 0, maxWidth: "100%" },
            "& .RaLayout-content": {
              backgroundColor: bg,
              padding: "20px 24px 32px",
              minWidth: 0,
              maxWidth: "100%",
              overflowX: "hidden",
            },
            "@media (max-width:599.95px)": {
              "& .RaLayout-content": { padding: "12px 16px 24px" },
            },
          },
        },
      },
      RaList: {
        styleOverrides: {
          root: {
            "& .RaList-main": { minWidth: 0, maxWidth: "100%" },
            "& .RaList-content": {
              borderRadius: 16,
              border: `1px solid ${border}`,
              boxShadow: "none",
              overflowX: "auto",
              maxWidth: "100%",
            },
            "& .RaList-actions, & .MuiToolbar-root.RaTopToolbar-root": { backgroundColor: "transparent" },
            "& .RaList-actions": { flexWrap: "wrap" },
          },
        },
      },
      RaDatagrid: {
        styleOverrides: {
          root: {
            "& .RaDatagrid-headerCell": {
              backgroundColor: headBg,
              fontWeight: 600,
              fontSize: 12,
              color: inkSoft,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            },
            "& .RaDatagrid-rowCell": { fontSize: 14 },
            "& .RaDatagrid-row:hover": { backgroundColor: hover },
            "& .RaDatagrid-clickableRow": { cursor: "pointer" },
          },
        },
      },
      RaShow: {
        styleOverrides: {
          root: {
            "& .RaShow-main": { minWidth: 0 },
            "& .RaShow-card": {
              minWidth: 0,
              borderRadius: 16,
              border: `1px solid ${border}`,
              boxShadow: "none",
            },
          },
        },
      },
      RaEdit: {
        styleOverrides: {
          root: { "& .RaEdit-main": { minWidth: 0 }, "& .RaEdit-card": { minWidth: 0, borderRadius: 16, border: `1px solid ${border}`, boxShadow: "none" } },
        },
      },
      RaCreate: {
        styleOverrides: {
          root: { "& .RaCreate-card": { borderRadius: 16, border: `1px solid ${border}`, boxShadow: "none" } },
        },
      },
      RaEmpty: {
        styleOverrides: {
          root: {
            "& .RaEmpty-message": { opacity: 1, color: inkSoft, marginTop: 48 },
            "& .RaEmpty-icon": { width: 56, height: 56, opacity: 0.35 },
            "& .MuiTypography-h4": { fontSize: "1.1rem", fontWeight: 600 },
          },
        },
      },
      RaTopToolbar: {
        styleOverrides: { root: { alignItems: "center", gap: 4 } },
      },
      RaFilterForm: {
        styleOverrides: { root: { alignItems: "center" } },
      },
      RaMenuItemLink: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: "1px 8px",
            paddingTop: 6,
            paddingBottom: 6,
            color: inkSoft,
            "&.RaMenuItemLink-active": {
              backgroundColor: alpha(primary, dark ? 0.18 : 0.1),
              color: primary,
              fontWeight: 600,
              "& .RaMenuItemLink-icon": { color: primary },
            },
          },
          icon: { minWidth: 34, color: inkSoft },
        },
      },
      RaSidebar: {
        styleOverrides: {
          root: {
            "& .RaSidebar-fixed": {
              backgroundColor: paper,
              borderRight: `1px solid ${border}`,
            },
          },
        },
      },
      RaLabeled: {
        styleOverrides: {
          root: {
            "& .RaLabeled-label": { fontSize: 12, fontWeight: 600, color: inkSoft },
          },
        },
      },
    },
  });
};

export const lightTheme = buildTheme("light");
export const darkTheme = buildTheme("dark");

export default lightTheme;
